/**
 * Integrytas Group - Common Security Module (CSM)
 * Supabase client wrapper.
 *
 * Notes:
 *  - Only the ANON key is exposed to the browser via VITE_ variables.
 *  - The SERVICE ROLE key must never live here; it stays on server code.
 *  - We forensically thread `real_admin_id` through `x-csm-real-admin-id`
 *    header so server-side functions and Postgres triggers can stamp the
 *    correct actor during admin mimic sessions.
 */

import { createClient, type SupabaseClient } from '@supabase/supabase-js';

const SUPABASE_URL      = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  // Fail fast in dev - prevents accidental anonymous-only deploys.
   
  console.error(
    '[CSM] Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY. ' +
    'Populate .env before running `npm run dev`.',
  );
}

export const supabase: SupabaseClient = createClient(
  SUPABASE_URL ?? '',
  SUPABASE_ANON_KEY ?? '',
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storageKey: 'csm.supabase.auth',
    },
    global: {
      headers: {
        'x-csm-client': 'csm-web',
      },
    },
  },
);

/**
 * Returns a Supabase client variant that injects the real admin id during a
 * mimic session. Use this for any write that must be forensically attributed
 * to the human admin (NOT the impersonated user).
 */
export function supabaseAsAdmin(realAdminId: number): SupabaseClient {
  return createClient(SUPABASE_URL ?? '', SUPABASE_ANON_KEY ?? '', {
    auth: { persistSession: false, autoRefreshToken: false },
    global: {
      headers: {
        'x-csm-client': 'csm-web',
        'x-csm-real-admin-id': String(realAdminId),
      },
    },
  });
}
